import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class VoiceCampaigns extends LitElement {
  static properties = {
    campaigns: { type: Array },
    selectedCampaign: { type: Object },
    activeTab: { type: String }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-header-left {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .page-subtitle {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      /* ─── Campaign Cards Grid ─── */
      .campaigns-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
        gap: var(--space-5);
      }

      .campaign-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        padding: var(--space-6);
        border-radius: var(--radius-xl);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        cursor: default;
        position: relative;
        overflow: hidden;
      }

      .campaign-card:hover {
        transform: translateY(-3px);
        box-shadow: var(--shadow-hover-lift);
      }

      /* Card Header */
      .campaign-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-3);
      }

      .campaign-name {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .pulse-dot {
        display: inline-block;
        width: 6px;
        height: 6px;
        border-radius: var(--radius-full);
        background: var(--color-success);
        animation: softPulse 2.5s ease-in-out infinite;
      }

      /* Progress Bar */
      .progress-section {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .progress-bar-track {
        flex: 1;
        height: 8px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        overflow: hidden;
      }

      .progress-bar-fill {
        height: 100%;
        border-radius: var(--radius-full);
        background: var(--color-primary);
        transition: width var(--duration-slow) var(--ease-out);
      }

      .progress-bar-fill.complete {
        background: var(--color-success);
      }

      .progress-count {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        white-space: nowrap;
      }

      /* Stats Row */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-3);
      }

      .mini-stat {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-tertiary);
        border-radius: var(--radius-md);
      }

      .mini-stat-icon {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        width: 16px;
        text-align: center;
      }

      .mini-stat-icon.success { color: var(--color-success); }
      .mini-stat-icon.error { color: var(--color-error); }
      .mini-stat-icon.warning { color: var(--color-warning); }
      .mini-stat-icon.neutral { color: var(--color-text-tertiary); }

      .mini-stat-info {
        display: flex;
        flex-direction: column;
      }

      .mini-stat-value {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        line-height: 1;
      }

      .mini-stat-label {
        font-family: var(--font-body);
        font-size: 0.625rem;
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      /* Card Footer */
      .campaign-card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: var(--space-3);
        border-top: 1px solid var(--color-border-subtle);
      }

      .campaign-meta {
        display: flex;
        align-items: center;
        gap: var(--space-4);
      }

      .campaign-meta-item {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .campaign-meta-item i {
        font-size: var(--text-xs);
      }

      .campaign-actions {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .action-btn {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .action-btn.danger:hover {
        background: var(--color-error-light);
        color: var(--color-error);
      }

      /* ─── Detail View ─── */
      .detail-view {
        animation: slideUp var(--duration-slow) var(--ease-out) both;
      }

      .back-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-tertiary);
        background: none;
        border: none;
        cursor: pointer;
        padding: var(--space-1) 0;
        margin-bottom: var(--space-4);
        transition: color var(--transition-fast);
      }

      .back-btn:hover {
        color: var(--color-text-primary);
      }

      .detail-header {
        margin-bottom: var(--space-6);
      }

      .detail-header-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .detail-title-row {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .detail-title {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .detail-progress {
        max-width: 480px;
      }

      /* KPI Cards */
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-4);
        margin-bottom: var(--space-8);
      }

      .kpi-card {
        padding: var(--space-6);
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-md);
      }

      .kpi-card .metric-value {
        font-size: var(--text-2xl);
      }

      .kpi-icon {
        width: 36px;
        height: 36px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-base);
        margin-bottom: var(--space-3);
      }

      .kpi-icon.primary {
        background: var(--color-primary-light);
        color: var(--color-primary);
      }

      .kpi-icon.success {
        background: var(--color-success-light);
        color: var(--color-success);
      }

      .kpi-icon.accent {
        background: var(--color-accent-light);
        color: var(--color-accent);
      }

      .kpi-icon.warning {
        background: var(--color-warning-light);
        color: var(--color-warning);
      }

      /* Results Table */
      .table-container {
        overflow-x: auto;
        border-radius: var(--radius-xl);
        border: 1px solid rgba(255, 255, 255, 0.35);
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .status-cell {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .sentiment-positive { color: var(--color-success); }
      .sentiment-negative { color: var(--color-error); }
      .sentiment-neutral { color: var(--color-text-tertiary); }

      /* Settings Tab */
      .settings-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-5);
      }

      .setting-group {
        padding: var(--space-5);
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-md);
      }

      .setting-group-title {
        font-family: var(--font-display);
        font-size: var(--text-base);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        margin-bottom: var(--space-4);
        letter-spacing: var(--tracking-tight);
      }

      .setting-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) 0;
        border-bottom: 1px solid var(--color-border-subtle);
      }

      .setting-row:last-child {
        border-bottom: none;
      }

      .setting-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .setting-value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
      }

      /* Contact List Tab */
      .contacts-summary {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        margin-bottom: var(--space-5);
      }

      .contacts-summary-stat {
        display: flex;
        flex-direction: column;
        gap: var(--space-0\.5);
      }

      .contacts-summary-stat .value {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
      }

      .contacts-summary-stat .label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .contacts-divider {
        width: 1px;
        height: 36px;
        background: var(--color-border-primary);
      }

      /* ─── Responsive ─── */
      @media (max-width: 920px) {
        .campaigns-grid {
          grid-template-columns: 1fr;
        }
        .stats-row {
          grid-template-columns: repeat(2, 1fr);
        }
        .kpi-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .settings-grid {
          grid-template-columns: 1fr;
        }
      }
    `
  ];

  constructor() {
    super();
    this.activeTab = 'results';
    this.selectedCampaign = null;
    this.campaigns = [
      {
        id: 1,
        name: 'Q1 Appointment Reminders',
        status: 'active',
        agent: 'Appointment Setter',
        createdAt: '2026-01-15',
        totalCalls: 500,
        completedCalls: 234,
        failedCalls: 12,
        voicemailCalls: 45,
        results: [
          { phone: '+1 (555) 234-5678', status: 'completed', duration: '2:34', outcome: 'Confirmed', sentiment: 'positive', time: '10:23 AM' },
          { phone: '+1 (555) 345-6789', status: 'voicemail', duration: '0:31', outcome: 'Left Message', sentiment: 'neutral', time: '10:25 AM' },
          { phone: '+1 (555) 456-7890', status: 'completed', duration: '3:12', outcome: 'Rescheduled', sentiment: 'positive', time: '10:28 AM' },
          { phone: '+1 (555) 567-8901', status: 'failed', duration: '0:00', outcome: 'No Answer', sentiment: 'neutral', time: '10:30 AM' },
          { phone: '+1 (555) 678-9012', status: 'completed', duration: '1:45', outcome: 'Confirmed', sentiment: 'positive', time: '10:33 AM' },
          { phone: '+1 (555) 789-0123', status: 'completed', duration: '4:02', outcome: 'Cancelled', sentiment: 'negative', time: '10:36 AM' },
          { phone: '+1 (555) 890-1234', status: 'voicemail', duration: '0:28', outcome: 'Left Message', sentiment: 'neutral', time: '10:38 AM' },
          { phone: '+1 (555) 901-2345', status: 'completed', duration: '2:15', outcome: 'Confirmed', sentiment: 'positive', time: '10:41 AM' }
        ],
        settings: {
          schedule: 'Weekdays 9:00 AM - 5:00 PM EST',
          maxRetries: 3,
          retryDelay: '30 minutes',
          concurrency: 5,
          callerId: '+1 (555) 100-0001',
          voicemailDetection: true,
          maxCallDuration: '5 minutes'
        },
        contacts: { total: 500, valid: 487, invalid: 13 }
      },
      {
        id: 2,
        name: 'Holiday Promo Outreach',
        status: 'completed',
        agent: 'Sales Outbound',
        createdAt: '2025-12-01',
        totalCalls: 1200,
        completedCalls: 1200,
        failedCalls: 89,
        voicemailCalls: 312,
        results: [
          { phone: '+1 (555) 111-2233', status: 'completed', duration: '3:45', outcome: 'Interested', sentiment: 'positive', time: '9:05 AM' },
          { phone: '+1 (555) 222-3344', status: 'completed', duration: '1:20', outcome: 'Not Interested', sentiment: 'negative', time: '9:08 AM' },
          { phone: '+1 (555) 333-4455', status: 'voicemail', duration: '0:30', outcome: 'Left Message', sentiment: 'neutral', time: '9:10 AM' },
          { phone: '+1 (555) 444-5566', status: 'completed', duration: '2:55', outcome: 'Follow Up', sentiment: 'positive', time: '9:14 AM' },
          { phone: '+1 (555) 555-6677', status: 'failed', duration: '0:00', outcome: 'Invalid Number', sentiment: 'neutral', time: '9:16 AM' }
        ],
        settings: {
          schedule: 'Daily 10:00 AM - 8:00 PM EST',
          maxRetries: 2,
          retryDelay: '1 hour',
          concurrency: 10,
          callerId: '+1 (555) 100-0002',
          voicemailDetection: true,
          maxCallDuration: '8 minutes'
        },
        contacts: { total: 1200, valid: 1156, invalid: 44 }
      },
      {
        id: 3,
        name: 'New Customer Welcome Calls',
        status: 'draft',
        agent: 'Welcome Agent',
        createdAt: '2026-02-08',
        totalCalls: 300,
        completedCalls: 0,
        failedCalls: 0,
        voicemailCalls: 0,
        results: [],
        settings: {
          schedule: 'Weekdays 10:00 AM - 4:00 PM EST',
          maxRetries: 2,
          retryDelay: '2 hours',
          concurrency: 3,
          callerId: '+1 (555) 100-0003',
          voicemailDetection: true,
          maxCallDuration: '6 minutes'
        },
        contacts: { total: 300, valid: 298, invalid: 2 }
      }
    ];
  }

  _getStatusBadge(status) {
    const map = {
      active: { cls: 'badge-success', label: 'Active', dot: true },
      completed: { cls: 'badge-neutral', label: 'Completed', dot: false },
      paused: { cls: 'badge-warning', label: 'Paused', dot: false },
      draft: { cls: 'badge-info', label: 'Draft', dot: false }
    };
    const info = map[status] || map.draft;
    return html`
      <span class="badge ${info.cls}">
        ${info.dot ? html`<span class="pulse-dot"></span>` : ''}
        ${info.label}
      </span>
    `;
  }

  _getProgressPercent(c) {
    if (c.totalCalls === 0) return 0;
    return Math.round((c.completedCalls / c.totalCalls) * 100);
  }

  _getRemainingCalls(c) {
    return c.totalCalls - c.completedCalls - c.failedCalls - c.voicemailCalls;
  }

  _selectCampaign(c) {
    this.selectedCampaign = { ...c };
    this.activeTab = 'results';
  }

  _backToList() {
    this.selectedCampaign = null;
  }

  _setTab(tab) {
    this.activeTab = tab;
  }

  // ─── List View ───
  _renderCampaignList() {
    return html`
      <div class="page-header">
        <div class="page-header-left">
          <h1 class="page-title">Campaigns</h1>
          <p class="page-subtitle">Manage and monitor your outbound call campaigns</p>
        </div>
        <button class="btn btn-primary" @click=${() => {}}>
          <i class="fa-sharp fa-light fa-plus"></i>
          Create Campaign
        </button>
      </div>

      <div class="campaigns-grid">
        ${this.campaigns.map(c => this._renderCampaignCard(c))}
      </div>
    `;
  }

  _renderCampaignCard(c) {
    const pct = this._getProgressPercent(c);
    const remaining = this._getRemainingCalls(c);
    return html`
      <div class="card campaign-card">
        <!-- Header -->
        <div class="campaign-card-header">
          <span class="campaign-name">${c.name}</span>
          ${this._getStatusBadge(c.status)}
        </div>

        <!-- Progress -->
        <div class="progress-section">
          <div class="progress-bar-track">
            <div
              class="progress-bar-fill ${c.status === 'completed' ? 'complete' : ''}"
              style="width: ${pct}%"
            ></div>
          </div>
          <span class="progress-count">${c.completedCalls} / ${c.totalCalls} calls</span>
        </div>

        <!-- Stats -->
        <div class="stats-row">
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-circle-check mini-stat-icon success"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${c.completedCalls}</span>
              <span class="mini-stat-label">Completed</span>
            </div>
          </div>
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-circle-xmark mini-stat-icon error"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${c.failedCalls}</span>
              <span class="mini-stat-label">Failed</span>
            </div>
          </div>
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-voicemail mini-stat-icon warning"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${c.voicemailCalls}</span>
              <span class="mini-stat-label">Voicemail</span>
            </div>
          </div>
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-hourglass-half mini-stat-icon neutral"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${remaining >= 0 ? remaining : 0}</span>
              <span class="mini-stat-label">Remaining</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="campaign-card-footer">
          <div class="campaign-meta">
            <span class="campaign-meta-item">
              <i class="fa-sharp fa-light fa-robot"></i>
              ${c.agent}
            </span>
            <span class="campaign-meta-item">
              <i class="fa-sharp fa-light fa-calendar"></i>
              ${c.createdAt}
            </span>
          </div>
          <div class="campaign-actions">
            ${c.status === 'active' ? html`
              <button class="action-btn" title="Pause" @click=${(e) => { e.stopPropagation(); }}>
                <i class="fa-sharp fa-light fa-pause"></i>
              </button>
            ` : c.status === 'paused' ? html`
              <button class="action-btn" title="Resume" @click=${(e) => { e.stopPropagation(); }}>
                <i class="fa-sharp fa-light fa-play"></i>
              </button>
            ` : ''}
            ${c.status !== 'draft' && c.status !== 'completed' ? html`
              <button class="action-btn danger" title="Stop" @click=${(e) => { e.stopPropagation(); }}>
                <i class="fa-sharp fa-light fa-stop"></i>
              </button>
            ` : ''}
            <button class="action-btn" title="View Details" @click=${() => this._selectCampaign(c)}>
              <i class="fa-sharp fa-light fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ─── Detail View ───
  _renderDetailView() {
    const c = this.selectedCampaign;
    const pct = this._getProgressPercent(c);
    return html`
      <div class="detail-view">
        <button class="back-btn" @click=${this._backToList}>
          <i class="fa-sharp fa-light fa-arrow-left"></i>
          Back to Campaigns
        </button>

        <!-- Header -->
        <div class="detail-header">
          <div class="detail-header-top">
            <div class="detail-title-row">
              <h1 class="detail-title">${c.name}</h1>
              ${this._getStatusBadge(c.status)}
            </div>
          </div>
          <div class="detail-progress progress-section">
            <div class="progress-bar-track">
              <div
                class="progress-bar-fill ${c.status === 'completed' ? 'complete' : ''}"
                style="width: ${pct}%"
              ></div>
            </div>
            <span class="progress-count">${pct}% — ${c.completedCalls} / ${c.totalCalls} calls</span>
          </div>
        </div>

        <!-- Tab Bar -->
        <div class="tab-bar">
          <div
            class="tab-item ${this.activeTab === 'results' ? 'active' : ''}"
            @click=${() => this._setTab('results')}
          >
            <i class="fa-sharp fa-light fa-chart-bar"></i>
            Results
          </div>
          <div
            class="tab-item ${this.activeTab === 'settings' ? 'active' : ''}"
            @click=${() => this._setTab('settings')}
          >
            <i class="fa-sharp fa-light fa-gear"></i>
            Settings
          </div>
          <div
            class="tab-item ${this.activeTab === 'contacts' ? 'active' : ''}"
            @click=${() => this._setTab('contacts')}
          >
            <i class="fa-sharp fa-light fa-address-book"></i>
            Contact List
          </div>
        </div>

        <!-- Tab Content -->
        ${this.activeTab === 'results' ? this._renderResultsTab(c) : ''}
        ${this.activeTab === 'settings' ? this._renderSettingsTab(c) : ''}
        ${this.activeTab === 'contacts' ? this._renderContactsTab(c) : ''}
      </div>
    `;
  }

  _renderResultsTab(c) {
    const successRate = c.completedCalls > 0
      ? Math.round(((c.completedCalls - c.failedCalls) / c.completedCalls) * 100)
      : 0;
    const vmRate = c.totalCalls > 0
      ? Math.round((c.voicemailCalls / c.totalCalls) * 100)
      : 0;

    return html`
      <!-- KPI Cards -->
      <div class="kpi-grid">
        <div class="card kpi-card">
          <div class="kpi-icon primary">
            <i class="fa-sharp-duotone fa-solid fa-phone"></i>
          </div>
          <div class="metric-label">Total Calls</div>
          <div class="metric-value">${c.completedCalls + c.failedCalls + c.voicemailCalls}</div>
        </div>
        <div class="card kpi-card">
          <div class="kpi-icon success">
            <i class="fa-sharp-duotone fa-solid fa-chart-line-up"></i>
          </div>
          <div class="metric-label">Success Rate</div>
          <div class="metric-value">${successRate}%</div>
        </div>
        <div class="card kpi-card">
          <div class="kpi-icon accent">
            <i class="fa-sharp-duotone fa-solid fa-timer"></i>
          </div>
          <div class="metric-label">Avg Duration</div>
          <div class="metric-value">2:24</div>
        </div>
        <div class="card kpi-card">
          <div class="kpi-icon warning">
            <i class="fa-sharp-duotone fa-solid fa-voicemail"></i>
          </div>
          <div class="metric-label">Voicemail Rate</div>
          <div class="metric-value">${vmRate}%</div>
        </div>
      </div>

      <!-- Results Table -->
      ${c.results.length > 0 ? html`
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Outcome</th>
                <th>Sentiment</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              ${c.results.map(r => html`
                <tr>
                  <td style="font-family: var(--font-mono); font-size: var(--text-sm);">${r.phone}</td>
                  <td>
                    <div class="status-cell">
                      <span class="status-dot ${r.status === 'completed' ? 'active' : r.status === 'failed' ? 'error' : 'warning'}"></span>
                      ${r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                    </div>
                  </td>
                  <td style="font-family: var(--font-mono);">${r.duration}</td>
                  <td>${r.outcome}</td>
                  <td>
                    <span class="sentiment-${r.sentiment}">
                      <i class="fa-sharp fa-light ${r.sentiment === 'positive' ? 'fa-face-smile' : r.sentiment === 'negative' ? 'fa-face-frown' : 'fa-face-meh'}"></i>
                      ${r.sentiment.charAt(0).toUpperCase() + r.sentiment.slice(1)}
                    </span>
                  </td>
                  <td style="font-family: var(--font-mono); font-size: var(--text-xs); color: var(--color-text-tertiary);">${r.time}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      ` : html`
        <div class="empty-state">
          <div class="empty-state-icon">
            <i class="fa-sharp fa-light fa-chart-bar"></i>
          </div>
          <div class="empty-state-title">No results yet</div>
          <div class="empty-state-description">Results will appear here once the campaign starts running.</div>
        </div>
      `}
    `;
  }

  _renderSettingsTab(c) {
    const s = c.settings;
    return html`
      <div class="settings-grid">
        <div class="card setting-group">
          <div class="setting-group-title">
            <i class="fa-sharp fa-light fa-robot"></i>
            Agent & Schedule
          </div>
          <div class="setting-row">
            <span class="setting-label">Agent</span>
            <span class="setting-value">${c.agent}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Schedule</span>
            <span class="setting-value">${s.schedule}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Caller ID</span>
            <span class="setting-value">${s.callerId}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Max Call Duration</span>
            <span class="setting-value">${s.maxCallDuration}</span>
          </div>
        </div>

        <div class="card setting-group">
          <div class="setting-group-title">
            <i class="fa-sharp fa-light fa-arrows-rotate"></i>
            Retry & Concurrency
          </div>
          <div class="setting-row">
            <span class="setting-label">Max Retries</span>
            <span class="setting-value">${s.maxRetries}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Retry Delay</span>
            <span class="setting-value">${s.retryDelay}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Concurrency</span>
            <span class="setting-value">${s.concurrency} simultaneous</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Voicemail Detection</span>
            <span class="setting-value">
              <span class="toggle ${s.voicemailDetection ? 'active' : ''}"></span>
            </span>
          </div>
        </div>
      </div>
    `;
  }

  _renderContactsTab(c) {
    return html`
      <div class="contacts-summary">
        <div class="contacts-summary-stat">
          <span class="value">${c.contacts.total}</span>
          <span class="label">Total Contacts</span>
        </div>
        <div class="contacts-divider"></div>
        <div class="contacts-summary-stat">
          <span class="value">${c.contacts.valid}</span>
          <span class="label">Valid Numbers</span>
        </div>
        <div class="contacts-divider"></div>
        <div class="contacts-summary-stat">
          <span class="value">${c.contacts.invalid}</span>
          <span class="label">Invalid</span>
        </div>
      </div>

      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Phone Number</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Last Attempt</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            ${(c.results || []).slice(0, 5).map((r, i) => html`
              <tr>
                <td style="font-family: var(--font-mono);">${r.phone}</td>
                <td>
                  <span class="badge ${r.status === 'completed' ? 'badge-success' : r.status === 'failed' ? 'badge-error' : 'badge-warning'}">
                    ${r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                  </span>
                </td>
                <td style="font-family: var(--font-mono);">${i < 2 ? '1' : '2'}</td>
                <td style="font-family: var(--font-mono); font-size: var(--text-xs); color: var(--color-text-tertiary);">${r.time}</td>
                <td>${r.outcome}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  render() {
    return html`
      ${iconStyles}
      ${this.selectedCampaign
        ? this._renderDetailView()
        : this._renderCampaignList()}
    `;
  }
}

customElements.define('voice-campaigns', VoiceCampaigns);
